import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, Minus, Plus, ShoppingCart, ShieldCheck, Truck, RotateCcw, Lock, Heart, ChevronRight, Baby } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { getProductById, products } from '../data/products';
import { useCart } from '../context/CartContext';

const Product = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const foundProduct = getProductById(productId);
    if (foundProduct) {
      setProduct(foundProduct);
    }
  }, [productId]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Product Not Found
          </h2>
          <Link to="/category/all">
            <Button className="rounded-full bg-sky-500 hover:bg-sky-600">
              Browse All Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/cart');
  };

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const discount = product.originalPrice 
    ? Math.round((1 - product.price / product.originalPrice) * 100) 
    : 0;

  const trustBadges = [
    { icon: ShieldCheck, text: 'Safe & Tested' },
    { icon: Truck, text: 'Fast Shipping' },
    { icon: RotateCcw, text: '30-Day Returns' },
    { icon: Lock, text: 'Secure Checkout' },
  ];

  // Mock product images (using same image for demo)
  const productImages = [product.image, product.image, product.image];

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-sky-500">Home</Link>
            <ChevronRight size={16} />
            <Link to="/category/all" className="hover:text-sky-500">Shop</Link>
            <ChevronRight size={16} />
            <Link to={`/category/${product.category}`} className="hover:text-sky-500 capitalize">
              {product.category.replace('-', ' ')}
            </Link>
            <ChevronRight size={16} />
            <span className="text-slate-800 truncate max-w-[200px]">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-3xl overflow-hidden bg-slate-100">
              <img
                src={productImages[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex gap-4">
              {productImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                    selectedImage === index ? 'border-sky-500' : 'border-slate-200'
                  }`}
                  data-testid={`product-image-thumb-${index}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {discount > 0 && (
                <span className="bg-orange-400 text-white text-sm font-bold px-3 py-1 rounded-full">
                  SAVE {discount}%
                </span>
              )}
              {product.isBestseller && (
                <span className="bg-sky-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                  BESTSELLER
                </span>
              )}
            </div>

            {/* Title */}
            <h1 
              className="text-3xl md:text-4xl font-bold text-slate-800"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="product-title"
            >
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={20}
                    className={i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}
                  />
                ))}
              </div>
              <span className="text-slate-600">
                {product.rating} ({product.reviews} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-4">
              <span 
                className="text-4xl font-bold text-slate-800"
                style={{ fontFamily: 'Quicksand, sans-serif' }}
                data-testid="product-price"
              >
                ${product.price.toFixed(2)}
              </span>
              {product.originalPrice && (
                <span className="text-xl text-slate-400 line-through">
                  ${product.originalPrice.toFixed(2)}
                </span>
              )}
            </div>

            {/* Description */}
            <p 
              className="text-slate-600 text-lg"
              style={{ fontFamily: 'Quicksand, sans-serif' }}
            >
              {product.description}
            </p>

            {/* Features */}
            <div className="space-y-2">
              <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Key Features:
              </h3>
              <ul className="space-y-1">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-slate-600">
                    <div className="w-2 h-2 bg-sky-500 rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Age & Safety */}
            <div className="flex flex-wrap gap-4 py-4 border-y border-slate-100">
              <div className="flex items-center gap-2">
                <Baby className="text-sky-500" size={20} />
                <span className="text-slate-700">Ages: {product.ageGroup} years</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-green-500" size={20} />
                <span className="text-slate-700">Safety Tested</span>
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="font-medium text-slate-700">Quantity:</span>
                <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full h-10 w-10"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="quantity-decrease"
                  >
                    <Minus size={18} />
                  </Button>
                  <span className="w-12 text-center font-bold" data-testid="quantity-value">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full h-10 w-10"
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="quantity-increase"
                  >
                    <Plus size={18} />
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  className="flex-1 rounded-full py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg shadow-[0px_4px_0px_0px_#cc8400] hover:shadow-[0px_2px_0px_0px_#cc8400] active:shadow-none transition-all"
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="mr-2" size={20} />
                  Add to Cart
                </Button>
                <Button
                  className="flex-1 rounded-full py-6 bg-sky-500 hover:bg-sky-600 text-white font-bold text-lg"
                  onClick={handleBuyNow}
                  data-testid="buy-now-btn"
                >
                  Buy Now
                </Button>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-600">
                  <badge.icon className="text-sky-500" size={18} />
                  <span className="text-sm">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div className="mt-16">
          <h2 
            className="text-2xl md:text-3xl font-bold text-slate-800 mb-8"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Customer Reviews
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { name: 'Sarah M.', rating: 5, text: 'My kids absolutely love this toy! Great quality and exactly as described. Fast shipping too!' },
              { name: 'Michael R.', rating: 5, text: 'Perfect gift for my nephew. The quality is excellent and it keeps him entertained for hours.' },
              { name: 'Jennifer L.', rating: 4, text: 'Very satisfied with this purchase. Would definitely recommend to other parents.' },
              { name: 'David K.', rating: 5, text: 'Outstanding product! The build quality is amazing and my daughter plays with it every day.' },
            ].map((review, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-6">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'} />
                  ))}
                </div>
                <p className="text-slate-700 mb-3" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  "{review.text}"
                </p>
                <p className="font-semibold text-slate-800">{review.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-800 mb-8"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Add to Cart (Mobile) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 md:hidden z-50">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-xl font-bold text-slate-800">${(product.price * quantity).toFixed(2)}</p>
          </div>
          <Button
            className="rounded-full px-8 py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold shadow-[0px_4px_0px_0px_#cc8400]"
            onClick={handleAddToCart}
            data-testid="sticky-add-to-cart-btn"
          >
            <ShoppingCart className="mr-2" size={20} />
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Product;
